import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatCount, formatTime, mutate } from '../api';
import { useLoad } from '../hooks';
import { can, useIdentity } from '../context';
import { ConfirmButton, Empty, ErrorPanel, Loading, PageHeader, StatusBadge } from '../components/Common';

const DEVICE_PAGE_SIZE = 50;

export function DevicesPage() {
  const [params, setParams] = useSearchParams();
  const identity = useIdentity();
  const [query, setQuery] = useState(params.get('query') || '');
  const [busyId, setBusyId] = useState('');
  const key = params.toString();
  const requestKey = useMemo(() => {
    const next = new URLSearchParams(params);
    next.set('limit', String(DEVICE_PAGE_SIZE));
    return next.toString();
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const state = useLoad(() => api<any>(`/devices?${requestKey}`), [requestKey]);
  const status = params.get('status') || '';
  const items = useMemo(() => state.data?.items || [], [state.data]);
  const apply = () => {
    const next = new URLSearchParams(params);
    query ? next.set('query', query) : next.delete('query');
    next.delete('cursor');
    next.delete('limit');
    setParams(next);
  };
  const setStatus = (value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set('status', value) : next.delete('status');
    next.delete('cursor');
    next.delete('limit');
    setParams(next);
  };
  const deleteDevice = async (id: string) => {
    const reason = window.prompt('请输入删除原因', '管理员删除设备');
    if (!reason) return;
    setBusyId(id);
    try {
      await mutate(`/devices/${encodeURIComponent(id)}`, 'DELETE', { confirmation: '删除设备', reason }, crypto.randomUUID());
      await state.reload();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : String(error));
    } finally {
      setBusyId('');
    }
  };
  return <>
    <PageHeader title="设备与授权" description="查看机器、客户端版本、授权状态与当天任务数" actions={<button disabled={state.loading} onClick={state.reload}>{state.loading ? '刷新中' : '刷新'}</button>} />
    <div className="panel filters">
      <div className="search-box"><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && apply()} placeholder="搜索设备 ID、机器码或版本" /><button onClick={apply}>搜索</button></div>
      <select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部状态</option><option value="pending">待审批</option><option value="active">有效</option><option value="disabled">停用</option><option value="expired">过期</option><option value="revoked">撤销</option><option value="missing">未授权</option></select>
    </div>
    {state.loading && !state.data ? <Loading /> : state.error ? <ErrorPanel message={state.error} onRetry={state.reload} /> : <div className="panel table-wrap">
      <table><thead><tr><th>设备</th><th>状态</th><th>客户端</th><th>授权期限</th><th>今日任务</th><th>最后在线</th><th /></tr></thead>
        <tbody>{items.map((row: any) => <tr key={row.id}>
          <td><strong className="mono">{row.machineCodeMasked}</strong><small className="block mono">{row.id}</small></td>
          <td><StatusBadge status={row.effectiveStatus} /></td>
          <td>{row.clientVersion || '-'}<small className="block">{row.platform}</small></td>
          <td>{row.isPermanent ? '永久' : formatTime(row.expiresAt)}<small className="block">v{row.licenseVersion || 0}</small></td>
          <td><strong>{formatCount(row.todayTotal)}</strong><small className="block">图 {formatCount(row.todayImages)} / 视 {formatCount(row.todayVideos)}</small></td>
          <td>{formatTime(row.lastSeenAt)}</td><td><div className="row-actions"><Link className="button-link" to={`/devices/${encodeURIComponent(row.id)}`}>详情</Link>{can(identity, 'devices.delete') && <ConfirmButton disabled={busyId === row.id} danger message={`确认删除设备 ${row.machineCodeMasked}？关联授权、用量和历史会一起删除。`} onConfirm={() => deleteDevice(row.id)}>删除</ConfirmButton>}</div></td>
        </tr>)}</tbody></table>{!items.length && <Empty text="没有符合条件的设备" />}
      {state.data?.nextCursor && <div className="pagination"><button disabled={state.loading} onClick={() => { const next = new URLSearchParams(params); next.set('cursor', state.data.nextCursor); next.delete('limit'); setParams(next); }}>下一页</button></div>}
    </div>}
  </>;
}
